import axios from "axios";
import _ from "lodash";

import store from "../../reduxStore";
import Swal from "helpers/swal";
import OrderActions from "../orders/actions";
import InvoiceActions from "../invoices/actions";
import AccountStatementsActions from "../accountStatements/actions";
import DeliveryActions from "../deliveries/actions";
import InspectionReportActions from "../inspectionReport/actions";
import QualityReportActions from "../qualityReport/actions";
import ReceiptsReturnsActions from "../receiptsReturns/actions";
import { getOptions } from "helpers/common";
import generateQuery from "helpers/query";
import IMeta from "models/IMeta";

export const actionTypes = {
  LOGOUT_USER_ACCOUNT: "LOGOUT_USER_ACCOUNT",
  USER_FOUND: "USER_FOUND",
  SET_APP_LOADING: "SET_APP_LOADING",
  GET_CUSTOMERS_REQUEST: "GET_CUSTOMERS_REQUEST",
  GET_CUSTOMERS_SUCCESS: "GET_CUSTOMERS_SUCCESS",
  GET_CUSTOMERS_FAILURE: "GET_CUSTOMERS_FAILURE",
  GET_ACCOUNT_MESSAGES_REQUEST: "GET_ACCOUNT_MESSAGES_REQUEST",
  GET_ACCOUNT_MESSAGES_SUCCESS: "GET_ACCOUNT_MESSAGES_SUCCESS",
  GET_ACCOUNT_MESSAGES_FAILURE: "GET_ACCOUNT_MESSAGES_FAILURE",
  GET_ROLE_IDS_REQUEST: "GET_ROLE_IDS_REQUEST",
  GET_ROLE_IDS_SUCCESS: "GET_ROLE_IDS_SUCCESS",
  GET_ROLE_IDS_FAILURE: "GET_ROLE_IDS_FAILURE",
  GET_MESSAGE_TYPES_REQUEST: "GET_MESSAGE_TYPES_REQUEST",
  GET_MESSAGE_TYPES_SUCCESS: "GET_MESSAGE_TYPES_SUCCESS",
  GET_MESSAGE_TYPES_FAILURE: "GET_MESSAGE_TYPES_FAILURE",
};

export const setLoading = (isAppLoading: boolean) => ({
  type: actionTypes.SET_APP_LOADING,
  payload: { isAppLoading },
});

export async function getDocument(url) {
  try {
    const { data } = await axios({
      method: "get",
      url,
      responseType: "blob",
    });

    return data;
  } catch (error) {
    return error;
  }
}

export async function markAsRead(EntityIds, TypeId = "Order") {
  if (_.isEmpty(EntityIds)) {
    return;
  }

  try {
    const {
      app: { accountMessages, messageTypes, user },
    } = store.getState();
    const isRegularUser =
      _.get(user, "profile.CNBMedewerker", "false") === "false";

    if (accountMessages && isRegularUser) {
      await axios.all(
        EntityIds.map((EntityId) => {
          const accountMessage = _.find(accountMessages, {
            EntityId,
            TypeId: messageTypes[TypeId],
          }) as any;
          if (accountMessage) {
            return axios.patch(`AccountMessages(${accountMessage.Id})`, {
              Gezien: true,
            });
          }
          return;
        })
      );

      // @ts-ignore
      store.dispatch(Actions.getAccountMessages());
      // @ts-ignore
      store.dispatch(getAction(TypeId));
    }
  } catch (error) {
    Swal.toast({ title: error.response, type: "error" });
  }
}

export async function markAsUnread(EntityIds, TypeId = "Order") {
  if (_.isEmpty(EntityIds)) {
    return;
  }

  try {
    const { accountMessages, messageTypes } = store.getState().app;

    if (accountMessages) {
      await axios.all(
        EntityIds.map((EntityId) => {
          const accountMessage = _.find(accountMessages, {
            EntityId,
            TypeId: messageTypes[TypeId],
          }) as any;
          if (accountMessage) {
            return axios.patch(`AccountMessages(${accountMessage.Id})`, {
              Gezien: false,
            });
          } else {
            return axios.post(`AccountMessages`, {
              TypeId,
              EntityId,
              Gezien: false,
              Gemaild: false,
            });
          }
        })
      );

      // @ts-ignore
      store.dispatch(Actions.getAccountMessages());
      // @ts-ignore
      store.dispatch(getAction(TypeId));
    }
  } catch (error) {
    Swal.toast({ title: error.response, type: "error" });
  }
}

export default class Actions {
  public static getCustomers(meta: IMeta = {}) {
    return async (dispatch, getState) => {
      if (_.isEmpty(meta)) {
        meta = _.get(getState(), "app.meta", {});
      }
      dispatch(request(meta.currentPage === 0));
      try {
        const { data } = await axios.get(
          `Customers?$expand=Certificates&$select=*&$orderby=Number${generateQuery(
            meta
          )}`
        );
        const customers = [...getState().app.customers, ...data.value];
        const newMeta = { ...meta, count: +_.get(data, "odata.count", 0) };
        dispatch(success(customers, newMeta));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request(payload) {
      return { type: actionTypes.GET_CUSTOMERS_REQUEST, payload };
    }
    function success(payload, meta) {
      return {
        type: actionTypes.GET_CUSTOMERS_SUCCESS,
        payload: { customer: payload, meta },
      };
    }
    function failure(error) {
      return { type: actionTypes.GET_CUSTOMERS_FAILURE, error };
    }
  }

  public static getMessageTypes() {
    return async (dispatch) => {
      dispatch(request());
      try {
        const { data } = await axios.get("AccountMessages/Enums");
        const options = data.find((o) => o.Name === "MessageType").Options;
        const messageTypes = options.reduce(
          (m, item) => ({ ...m, [item.Value]: item.Name }),
          {}
        );
        dispatch(success(messageTypes));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_MESSAGE_TYPES_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_MESSAGE_TYPES_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_MESSAGE_TYPES_FAILURE, error };
    }
  }

  public static getAccountMessages() {
    return async (dispatch) => {
      dispatch(request());
      try {
        const {
          data: { value },
        } = await axios.get(`AccountMessages?$inlinecount=allpages`);
        dispatch(success(value));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_ACCOUNT_MESSAGES_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_ACCOUNT_MESSAGES_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_ACCOUNT_MESSAGES_FAILURE, error };
    }
  }

  public static getRoleIds() {
    return async (dispatch) => {
      dispatch(request());
      try {
        const { data } = await axios.get(`OrdersExtended/Enums`);
        const roleIds = _.sortBy(getOptions(data, "RelationRole"), "label");
        dispatch(success(roleIds));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_ROLE_IDS_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_ROLE_IDS_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_ROLE_IDS_FAILURE, error };
    }
  }
}

const getAction = (type: string) => {
  switch (type) {
    case "Factuur":
      return InvoiceActions.getInvoices();
    case "RekeningOverzicht":
      return AccountStatementsActions.getAccountStatements();
    case "Levering":
      return DeliveryActions.getDeliveries();
    case "Receipt":
      return InspectionReportActions.getInspectionReports();
    case "InspectionReport":
      return QualityReportActions.getQualityReports();
    case "ReceiptAndReturn":
      return ReceiptsReturnsActions.getReceipts();
    case "Teeladvies":
      return async () => undefined;
    default:
      return OrderActions.getOrders();
  }
};
